import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Wrench, Loader2, Check, X, CheckCircle2, XCircle } from "lucide-react";
import { api, money } from "@/lib/api";
import { NumberPlate } from "@/components/NumberPlate";

export default function Approve() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setData(await api(`/approvals/public/${token}`)); }
    catch (e) { setError(e.message || "This approval link is invalid or has expired."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [token]);

  const decide = async (decision) => {
    setSubmitting(true);
    try {
      const res = await api(`/approvals/public/${token}/decide`, { method: "POST", body: JSON.stringify({ decision, note }) });
      setResult(res.status);
    } catch (e) { setError(e.message || "Could not record your decision."); }
    finally { setSubmitting(false); }
  };

  const status = result || data?.status;
  const decided = status && status !== "pending";

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center gap-2.5 bg-midnight px-6 py-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white/10"><Wrench className="h-4 w-4 text-brand" /></div>
        <span className="font-display text-[15px] font-bold tracking-widest text-white">PRO GARAGES</span>
      </header>

      <div className="mx-auto max-w-xl px-4 py-10">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" />Loading request…</div>
        ) : error ? (
          <div className="rounded-xl border border-danger/30 bg-danger-bg p-6 text-center text-sm font-semibold text-danger">{error}</div>
        ) : (
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid="approval-card">
            <p className="text-[11px] font-extrabold tracking-widest text-brand">{data.garage?.name || "YOUR GARAGE"}</p>
            <h1 className="mt-1 font-display text-3xl font-bold uppercase text-midnight">Approval needed</h1>
            {data.vehicle?.vrm && <div className="mt-3"><NumberPlate vrm={data.vehicle.vrm} size="sm" /> <span className="ml-2 text-sm text-muted-foreground">{data.vehicle.make} {data.vehicle.model}</span></div>}

            <div className="mt-5 rounded-lg bg-secondary p-4">
              <p className="text-base font-bold text-midnight">{data.title}</p>
              {data.reason && <p className="mt-1 text-sm text-slate-600">{data.reason}</p>}
            </div>

            <div className="mt-4 divide-y divide-border border-t-2 border-midnight">
              {(data.items || []).map((it, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 text-sm">
                  <span className="text-midnight">{it.description}</span>
                  <span className="text-muted-foreground">{it.qty} × {money(it.unit_price)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between py-3">
                <span className="text-sm font-extrabold text-midnight">Total (inc. VAT)</span>
                <span className="font-display text-xl font-bold text-midnight">{money(data.totals?.gross)}</span>
              </div>
            </div>

            {decided ? (
              <div className={`mt-6 flex items-center gap-2 rounded-lg p-4 ${status === "approved" ? "bg-success/10 text-success" : "bg-danger-bg text-danger"}`} data-testid="approval-result">
                {status === "approved" ? <CheckCircle2 className="h-6 w-6" /> : <XCircle className="h-6 w-6" />}
                <p className="font-bold">You {status} this work. Thank you — your garage has been notified.</p>
              </div>
            ) : (
              <>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="approval-note" placeholder="Add a note for your garage (optional)…"
                  className="mt-5 w-full rounded-lg border border-input bg-card px-3.5 py-3 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" rows={2} />
                <div className="mt-4 flex gap-3">
                  <button disabled={submitting} onClick={() => decide("approve")} data-testid="approve-btn" className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-success py-3.5 text-sm font-extrabold text-white disabled:opacity-60">
                    <Check className="h-5 w-5" />APPROVE
                  </button>
                  <button disabled={submitting} onClick={() => decide("decline")} data-testid="decline-btn" className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-danger py-3.5 text-sm font-extrabold text-danger disabled:opacity-60">
                    <X className="h-5 w-5" />DECLINE
                  </button>
                </div>
              </>
            )}
            <p className="mt-5 text-center text-[11px] text-muted-foreground">This link is unique to you and stays active for 14 days.</p>
          </div>
        )}
      </div>
    </div>
  );
}
